/**
 * Redux slice for Computer-Based Assessments state management
 */

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../Shared/Redux/store';
import { ComputerAssessment } from '../Shared/API/AssessmentsApi';

interface AssessmentsState {
  selectedAssessment: ComputerAssessment | null;
  isCreateModalOpen: boolean;
  isPublishModalOpen: boolean;
  isConvertModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isUpdatePinModalOpen: boolean;
  modalMode: 'create' | 'edit' | '';
  // Context for creation when opened from a specific page (e.g., Lesson Note)
  contextLessonId?: number;
  contextClassId?: number;
  contextSubjectId?: number;
  contextTitle?: string;
  contextLearningObjectives?: string;
  filters: {
    status?: string;
    purpose?: string;
    classId?: number;
    subjectId?: number;
    search?: string;
  };
}

const initialState: AssessmentsState = {
  selectedAssessment: null,
  isCreateModalOpen: false,
  isPublishModalOpen: false,
  isConvertModalOpen: false,
  isDeleteModalOpen: false,
  isUpdatePinModalOpen: false,
  modalMode: '',
  contextLessonId: undefined,
  contextClassId: undefined,
  contextSubjectId: undefined,
  contextTitle: undefined,
  contextLearningObjectives: undefined,
  filters: {},
};

const assessmentsSlice = createSlice({
  name: 'assessments',
  initialState,
  reducers: {
    setSelectedAssessment: (state, action: PayloadAction<ComputerAssessment | null>) => {
      state.selectedAssessment = action.payload;
    },
    
    toggleCreateModal: (state) => {
      state.isCreateModalOpen = !state.isCreateModalOpen;
      if (!state.isCreateModalOpen) {
        state.selectedAssessment = null;
        state.modalMode = '';
        state.contextLessonId = undefined;
        state.contextClassId = undefined;
        state.contextSubjectId = undefined;
        state.contextTitle = undefined;
        state.contextLearningObjectives = undefined;
      }
    },
    
    togglePublishModal: (state) => {
      state.isPublishModalOpen = !state.isPublishModalOpen;
    },
    
    toggleConvertModal: (state) => {
      state.isConvertModalOpen = !state.isConvertModalOpen;
    },
    
    toggleDeleteModal: (state) => {
      state.isDeleteModalOpen = !state.isDeleteModalOpen;
    },
    
    toggleUpdatePinModal: (state) => {
      state.isUpdatePinModalOpen = !state.isUpdatePinModalOpen;
    },
    
    setModalMode: (state, action: PayloadAction<'create' | 'edit' | ''>) => {
      state.modalMode = action.payload;
    },
    
    setFilters: (state, action: PayloadAction<Partial<AssessmentsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    clearFilters: (state) => {
      state.filters = {};
    },
    
    openEditModal: (state, action: PayloadAction<ComputerAssessment>) => {
      state.selectedAssessment = action.payload;
      state.modalMode = 'edit';
      state.isCreateModalOpen = true;
    },
    
    openCreateModal: (state, action: PayloadAction<{ lessonId?: number; classId?: number; subjectId?: number; title?: string; learningObjectives?: string } | undefined>) => {
      state.selectedAssessment = null;
      state.modalMode = 'create';
      state.isCreateModalOpen = true;
      state.contextLessonId = action.payload?.lessonId;
      state.contextClassId = action.payload?.classId;
      state.contextSubjectId = action.payload?.subjectId;
      state.contextTitle = action.payload?.title;
      state.contextLearningObjectives = action.payload?.learningObjectives;
    },
  },
});

// Selectors
export const selectSelectedAssessment = (state: RootState) => 
  state.assessments.selectedAssessment;

export const selectIsCreateModalOpen = (state: RootState) => 
  state.assessments.isCreateModalOpen;

export const selectIsPublishModalOpen = (state: RootState) => 
  state.assessments.isPublishModalOpen;

export const selectIsConvertModalOpen = (state: RootState) => 
  state.assessments.isConvertModalOpen;

export const selectIsDeleteModalOpen = (state: RootState) => 
  state.assessments.isDeleteModalOpen;

export const selectIsUpdatePinModalOpen = (state: RootState) =>
  state.assessments.isUpdatePinModalOpen;

export const selectModalMode = (state: RootState) => 
  state.assessments.modalMode;

export const selectFilters = (state: RootState) => 
  state.assessments.filters;

export const selectCreationContext = (state: RootState) => ({
  lessonId: state.assessments.contextLessonId,
  classId: state.assessments.contextClassId,
  subjectId: state.assessments.contextSubjectId,
  title: state.assessments.contextTitle,
  learningObjectives: state.assessments.contextLearningObjectives,
});


// Actions
export const {
  setSelectedAssessment,
  toggleCreateModal,
  togglePublishModal,
  toggleConvertModal,
  toggleDeleteModal,
  toggleUpdatePinModal,
  setModalMode,
  setFilters,
  clearFilters,
  openEditModal,
  openCreateModal,
} = assessmentsSlice.actions;

export default assessmentsSlice.reducer;
